import { DistanceUnit } from 'shared/src/model/common'
import { LanguageCode } from 'shared/src/util/language'
import { isValue } from 'shared/src/util/typeGuard'
import { getDefaultNumberFormatOptions, getTranslationStringForUnit } from 'shared/src/util/unit/unit'

export class Distance {
  private _millimeter: number

  constructor(value: number, unit: DistanceUnit) {
    this._millimeter = value * this.getUnitFactor(unit)
  }

  static millimeter(millimeter: number): Distance {
    return new Distance(millimeter, DistanceUnit.MILLIMETER)
  }

  static centimeter(centimeter: number): Distance {
    return new Distance(centimeter, DistanceUnit.CENTIMETER)
  }

  static zero(): Distance {
    return Distance.millimeter(0)
  }

  valueOf() {
    return this._millimeter
  }

  private getUnitFactor(unit: DistanceUnit): number {
    switch (unit) {
      case DistanceUnit.MILLIMETER:
        return 1
      case DistanceUnit.CENTIMETER:
        return 10
      case DistanceUnit.DECIMETER:
        return 100
      case DistanceUnit.METER:
        return 1000
      case DistanceUnit.KILOMETER:
        return 1000000
      case DistanceUnit.INCH:
        return 25.4
      case DistanceUnit.FOOT:
        return 304.8
      case DistanceUnit.YARD:
        return 914.4
      case DistanceUnit.MILE:
        return 1609344
      default:
        throw new Error('Unknown unit')
    }
  }

  to(targetUnit: DistanceUnit): number {
    return this._millimeter / this.getUnitFactor(targetUnit)
  }

  toRoundedDecimals(targetUnit: DistanceUnit, precision = 2): number {
    const targetValue = this.to(targetUnit)
    const decimals = this.getDecimals(targetUnit)
    if (targetUnit === DistanceUnit.CENTIMETER) {
      return Math.round(targetValue)
    }
    if (isValue(decimals)) {
      return Math.round(targetValue * decimals.length) / decimals.length
    }
    return parseFloat(targetValue.toFixed(precision))
  }

  private getDecimals(unit: DistanceUnit): number[] | null {
    switch (unit) {
      case DistanceUnit.INCH:
        return [0, 25, 50, 75]
      default:
        return null
    }
  }

  // Getters
  get millimeter(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.MILLIMETER)
  }

  get roundedMillimeter(): number {
    return Math.round(this._millimeter)
  }

  get centimeter(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.CENTIMETER)
  }

  get decimeter(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.DECIMETER)
  }

  get meter(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.METER)
  }

  get kilometer(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.KILOMETER)
  }

  get inch(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.INCH)
  }

  get foot(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.FOOT)
  }

  get yard(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.YARD)
  }

  get mile(): number {
    return this._millimeter / this.getUnitFactor(DistanceUnit.MILE)
  }

  toString(langCode = LanguageCode.ENGLISH): string {
    const options = getDefaultNumberFormatOptions(DistanceUnit.MILLIMETER)
    return Intl.NumberFormat(langCode, options).format(this._millimeter)
  }

  toStringWithUnit(distanceUnit = DistanceUnit.CENTIMETER, langCode = LanguageCode.ENGLISH): string {
    const value = this.toRoundedDecimals(distanceUnit)
    const options = getDefaultNumberFormatOptions(distanceUnit)
    return Intl.NumberFormat(langCode, options).format(value)
  }

  toStringWithUnitSuffixed(distanceUnit = DistanceUnit.CENTIMETER, langCode = LanguageCode.ENGLISH): string {
    const value = this.toRoundedDecimals(distanceUnit)
    const options = getDefaultNumberFormatOptions(distanceUnit)
    return `${Intl.NumberFormat(langCode, options).format(value)} ${getTranslationStringForUnit(distanceUnit)}`
  }

  // Operators
  add(other: Distance): Distance {
    return Distance.millimeter(this._millimeter + other._millimeter)
  }

  subtract(other: Distance): Distance {
    return Distance.millimeter(this._millimeter - other._millimeter)
  }

  multiply(factor: number): Distance {
    return Distance.millimeter(this._millimeter * factor)
  }

  divide(divisor: number): Distance {
    if (divisor === 0) {
      throw new Error('Cannot divide by zero')
    }
    return Distance.millimeter(this._millimeter / divisor)
  }

  equals(other: Distance): boolean {
    return this._millimeter === other._millimeter
  }

  greaterThan(other: Distance): boolean {
    return this._millimeter > other._millimeter
  }

  lessThan(other: Distance): boolean {
    return this._millimeter < other._millimeter
  }

  greaterThanOrEqual(other: Distance): boolean {
    return this._millimeter >= other._millimeter
  }

  lessThanOrEqual(other: Distance): boolean {
    return this._millimeter <= other._millimeter
  }

  negate(): Distance {
    return Distance.millimeter(-this._millimeter)
  }
}
